import { ArrowBack, Business, Search, Description, CheckCircle, Cancel, NavigateNext, NavigateBefore } from "@material-ui/icons";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import "react-tabs/style/react-tabs.css";
import { v4 as uuidv4 } from "uuid";

import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import AplicacaoPage from "./AplicacaoPage";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    HeaderActions,
    BackButton,
    NewButton,
    ResultsCard,
    SectionInfo,
    SectionInfoTitle,
    SectionSubtitle,
    TableWrapper,
    StreamsTable,
    TableHead,
    TableRow,
    TableCell,
    TableActions,
    ActionButton,
    StatusBadge,
    ConnectionBadge,
    EmptyState,
    EmptyTitle,
    EmptyText,
    getTheme,
} from "../../components/common";

function AplicacaoComerzzia() {
	const history = useHistory();
	const theme = getTheme('comerzzia');
	const [empresas, setEmpresas] = useState([]);
	const [isViewing, setIsViewing] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	// Filter state
	const [searchTerm, setSearchTerm] = useState('')

	// Sort state
	const [sortBy, setSortBy] = useState('name')
	const [sortOrder, setSortOrder] = useState('asc')

	// Pagination state
	const [recordsPerPage, setRecordsPerPage] = useState(10)
	const [currentPage, setCurrentPage] = useState(1)

	// Filtered data
	const filteredEmpresas = useMemo(() => {
		if (!searchTerm) return empresas
		const term = searchTerm.toLowerCase()
		return empresas.filter(item => {
			const nameMatch = item.name && item.name.toLowerCase().includes(term)
			const cnpjMatch = item.cnpj && item.cnpj.toLowerCase().includes(term)
			const descMatch = item.description && item.description.toLowerCase().includes(term)
			const idMatch = item.id && item.id.toLowerCase().includes(term)
			const grupoMatch = item.grupoId && item.grupoId.toLowerCase().includes(term)

			// Status match: check if search term matches "ativo" (for enabled=true) or "inativo" (for enabled=false)
			const statusMatch = (item.enabled === true && 'ativo'.includes(term)) ||
			                    (item.enabled === false && 'inativo'.includes(term)) ||
								(term === 'true' && item.enabled === true) ||
								(term === 'false' && item.enabled === false)

			return nameMatch || cnpjMatch || descMatch || idMatch || grupoMatch || statusMatch
		})
	}, [empresas, searchTerm])

	// Paginated data with sorting
	const paginatedEmpresas = useMemo(() => {
		const sorted = filteredEmpresas.slice().sort((a, b) => {
			let comparison = 0

			switch(sortBy) {
				case 'name':
					comparison = a.name.localeCompare(b.name)
					break
				case 'description':
					comparison = (a.description || '').localeCompare(b.description || '')
					break
				case 'status':
					comparison = (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1
					break
				case 'pos':
					comparison = (a.posList?.length || 0) - (b.posList?.length || 0)
					break
				default:
					comparison = a.name.localeCompare(b.name)
			}

			return sortOrder === 'asc' ? comparison : -comparison
		})

		const startIndex = (currentPage - 1) * recordsPerPage
		const endIndex = startIndex + recordsPerPage
		return sorted.slice(startIndex, endIndex)
	}, [filteredEmpresas, currentPage, recordsPerPage, sortBy, sortOrder])

	const totalPages = Math.ceil(filteredEmpresas.length / recordsPerPage)

	// Reset to first page when filters change
	useEffect(() => {
		setCurrentPage(1)
	}, [searchTerm, recordsPerPage])

	useEffect(() => {
		getAllAplicacoes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isViewing]);

	console.log('rendering comerzzia');

	const getAllAplicacoes = async () => {
		try {
			setIsLoading(true);
			const { data } = await fetchClient.get("/comerzzia/getAllAplicacao");
			console.log(data)
			setEmpresas(data);
		} catch (error) {
			console.log(error)
			message.error(error.response?.data?.message || "Erro interno, contate o suporte!");
		} finally {
			setIsLoading(false);
		}
	};

	if (isLoading) return <Loader />;

	return (
		<Container>
			{isViewing ? (
				<AplicacaoPage data={isViewing} isViewing={setIsViewing} />
			) : (
				<>
					<ModernHeader>
						<HeaderLeft>
							<BackButton color={theme.primary} onClick={() => history.push('/')}>
								<ArrowBack />
							</BackButton>
							<HeaderContent>
								<PageTitle>
									<Business style={{ marginRight: '12px', fontSize: '32px' }} />
									Aplicações Comerzzia
								</PageTitle>
								<PageSubtitle>Gerencie integrações e cadastros da Comerzzia</PageSubtitle>
							</HeaderContent>
						</HeaderLeft>
						<HeaderActions>
							<NewButton
								gradient={theme.gradient}
								onClick={() => {
									setIsViewing({ id: uuidv4(), newBusiness: true });
								}}
							>
								<PersonAddIcon style={{ marginRight: '8px' }} />
								Nova Aplicação
							</NewButton>
						</HeaderActions>
					</ModernHeader>

					{empresas.length === 0 ? (
						<ResultsCard>
							<EmptyState>
								<Description style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }} />
								<EmptyTitle>Nenhuma aplicação encontrada</EmptyTitle>
								<EmptyText>Não há aplicações Comerzzia cadastradas. Adicione uma nova aplicação para começar.</EmptyText>
							</EmptyState>
						</ResultsCard>
					) : (
						<ResultsCard>
							<SectionInfoWrapper>
								<SectionInfoLeft>
									<SectionInfoTitle>Aplicações cadastradas</SectionInfoTitle>
									<SectionSubtitle>
										Mostrando {paginatedEmpresas.length} de {filteredEmpresas.length} {filteredEmpresas.length === 1 ? 'aplicação' : 'aplicações'}
										{searchTerm && ` (filtrado de ${empresas.length})`}
									</SectionSubtitle>
								</SectionInfoLeft>
								<FilterControls>
									<SearchContainer>
										<SearchIconWrapper>
											<Search style={{ fontSize: '20px' }} />
										</SearchIconWrapper>
										<SearchInput
											type="text"
											placeholder="Pesquisar por ID, nome, descrição ou status..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
										{searchTerm && (
											<ClearSearchButton onClick={() => setSearchTerm('')}>
												×
											</ClearSearchButton>
										)}
									</SearchContainer>
								</FilterControls>
							</SectionInfoWrapper>

							<TableWrapper>
								<StreamsTable>
									<thead>
										<tr>
											{[
												{ key: 'id',          label: 'ID' },
												{ key: 'name',        label: 'Nome' },
												{ key: 'group',       label: 'Grupo' },
												{ key: 'description', label: 'Descrição' },
												{ key: 'pos',         label: 'POS' },
												{ key: 'status',      label: 'Status' },
											].map(col => (
												<TableHead
													key={col.key}
													onClick={() => {
														if (sortBy === col.key) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
														else { setSortBy(col.key); setSortOrder('asc'); }
													}}
													style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
												>
													{col.label}{sortBy === col.key ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
												</TableHead>
											))}
											<TableHead align="right">Ações</TableHead>
										</tr>
									</thead>
									<tbody>
										{paginatedEmpresas.length === 0 ? (
											<TableRow>
												<TableCell colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>
													Nenhum resultado encontrado para os filtros aplicados
												</TableCell>
											</TableRow>
										) : (
											paginatedEmpresas.map((aplicacao) => (
												<TableRow key={aplicacao.id}>
													<TableCell>
														<span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
															{aplicacao.id}
														</span>
													</TableCell>
													<TableCell>
														<strong>{aplicacao.name}</strong>
													</TableCell>
													<TableCell>
																{aplicacao.grupoId
																	? <GrupoBadge>{aplicacao.grupoId}</GrupoBadge>
																	: <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
																}
															</TableCell>
															<TableCell>{aplicacao.description || "Sem descrição"}</TableCell>
													<TableCell>
														<POSCountBadge>
															{aplicacao.posList ? aplicacao.posList.length : 0}
														</POSCountBadge>
													</TableCell>
													<TableCell>
														<StatusBadge active={aplicacao.enabled}>
															{aplicacao.enabled ? (
																<>
																	<CheckCircle style={{ fontSize: '14px', marginRight: '4px' }} />
																	Ativo
																</>
															) : (
																<>
																	<Cancel style={{ fontSize: '14px', marginRight: '4px' }} />
																	Inativo
																</>
															)}
														</StatusBadge>
													</TableCell>
													<TableActions>
														<ActionButton
															gradient={theme.gradient}
															onClick={() => setIsViewing(aplicacao)}
														>
															<Search style={{ marginRight: '6px', fontSize: '18px' }} />
															Visualizar
														</ActionButton>
													</TableActions>
												</TableRow>
											))
										)}
									</tbody>
								</StreamsTable>
							</TableWrapper>

							<PaginationWrapper>
								<RecordsPerPageContainer>
									<RecordsPerPageLabel>Registros por página:</RecordsPerPageLabel>
									<RecordsPerPageSelect
										value={recordsPerPage}
										onChange={(e) => setRecordsPerPage(Number(e.target.value))}
									>
										<option value={5}>5</option>
										<option value={10}>10</option>
										<option value={25}>25</option>
										<option value={50}>50</option>
										<option value={100}>100</option>
									</RecordsPerPageSelect>
								</RecordsPerPageContainer>

								<PaginationContainer>
									<PaginationButton
										disabled={currentPage === 1}
										onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
									>
										<NavigateBefore />
									</PaginationButton>
									<PageIndicator>
										Página {currentPage} de {totalPages || 1}
									</PageIndicator>
									<PaginationButton
										disabled={currentPage >= totalPages}
										onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
									>
										<NavigateNext />
									</PaginationButton>
								</PaginationContainer>
							</PaginationWrapper>
						</ResultsCard>
					)}
				</>
			)}
		</Container>
	);
}

export default AplicacaoComerzzia;

// ===== STYLED COMPONENTS =====

const SectionInfoWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding: 24px 16px 0 16px;
    gap: 16px;
    flex-wrap: wrap;
`;

const SectionInfoLeft = styled.div`
    display: flex;
    flex-direction: column;
`;

const FilterControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const SortContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SortLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
    font-weight: 500;
`;

const SortSelect = styled.select`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 150px;

    &:focus {
        outline: none;
        border-color: #00bcd4;
        box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
    }
`;

const SortOrderButton = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    color: #6b7280;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: #f8f9fa;
        border-color: #00bcd4;
        color: #00bcd4;
    }

    &:active {
        transform: scale(0.95);
    }
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    min-width: 300px;
`;

const SearchIconWrapper = styled.div`
    position: absolute;
    left: 12px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    pointer-events: none;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 40px 10px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #00bcd4;
        box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: #e5e7eb;
    color: #6b7280;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: #d1d5db;
        color: #374151;
    }
`;

const PaginationWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
`;

const RecordsPerPageContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RecordsPerPageLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const RecordsPerPageSelect = styled.select`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #00bcd4;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
`;

const PaginationButton = styled.button`
    width: 40px;
    height: 40px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%);
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 188, 212, 0.3);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        background: #bdbdbd;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const PageIndicator = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
    padding: 0 16px;
`;

const GrupoBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    font-family: 'Mulish', sans-serif;
    letter-spacing: 0.4px;
`;

const POSCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 24px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: rgba(0, 188, 212, 0.1);
    color: #00bcd4;
    font-family: 'Mulish', sans-serif;
`;
